import { Platform } from 'react-native';

export type MapsProvider = 'google' | 'apple';
export type MapsDirectionsMode = 'driving' | 'walking' | 'cycling' | 'motorcycle';

export function buildMapsUrl(
  dest: { latitude: number; longitude: number; address?: string },
  opts?: { origin?: { latitude: number; longitude: number }; mode?: MapsDirectionsMode; provider?: MapsProvider }
): string {
  const provider = opts?.provider ?? (Platform.OS === 'ios' ? 'apple' : 'google');
  if (provider === 'apple') {
    // Apple Maps has no cycling or motorcycle flag; 'r' (transit) is the closest
    // non-driving fallback for cycling, motorcycle falls back to 'd' (driving).
    const dirflg = opts?.mode === 'walking' ? 'w' : opts?.mode === 'cycling' ? 'r' : 'd';
    let url = `https://maps.apple.com/?daddr=${dest.latitude},${dest.longitude}&dirflg=${dirflg}`;
    if (opts?.origin) url += `&saddr=${opts.origin.latitude},${opts.origin.longitude}`;
    return url;
  }
  // Google Maps has no motorcycle travel mode, falls back to driving.
  const travelmode = opts?.mode === 'walking' ? 'walking' : opts?.mode === 'cycling' ? 'bicycling' : 'driving';
  let url = `https://www.google.com/maps/dir/?api=1&destination=${dest.latitude},${dest.longitude}&travelmode=${travelmode}`;
  if (opts?.origin) url += `&origin=${opts.origin.latitude},${opts.origin.longitude}`;
  return url;
}

const MAPS_LINK_RE = /(https?:\/\/)?((www\.)?google\.[a-z.]+\/maps|maps\.google\.[a-z.]+|goo\.gl\/maps|maps\.app\.goo\.gl|g\.co\/kgs|maps\.apple\.com|maps\.apple\/|apple\.co\/)/i;
const COORDS_RE = /^\s*(-?\d{1,2}(?:\.\d+)?)\s*[,\s]\s*(-?\d{1,3}(?:\.\d+)?)\s*$/;

/** True when the text is (or contains) a Google Maps / Apple Maps link. */
export function looksLikeMapsLink(text: string): boolean {
  return MAPS_LINK_RE.test((text || '').trim());
}

/** Plain "lat, lng" (or "lat lng") coordinates typed/pasted into a search box. */
export function parseCoordinateText(text: string): { latitude: number; longitude: number } | null {
  const m = COORDS_RE.exec(text || '');
  if (!m) return null;
  const latitude = parseFloat(m[1]);
  const longitude = parseFloat(m[2]);
  if (Math.abs(latitude) > 90 || Math.abs(longitude) > 180) return null;
  return { latitude, longitude };
}

/**
 * Coordinates embedded directly in a full Google/Apple Maps URL — no network
 * needed (…/@lat,lng,…, !3dlat!4dlng, ?q=/ll=/destination=/daddr=lat,lng,
 * /maps/search/lat,+lng). Short links (maps.app.goo.gl, apple.co, …) carry no
 * coordinates and return null — resolve those on the server.
 */
export function parseMapsLinkCoordinates(text: string): { latitude: number; longitude: number } | null {
  const raw = (text || '').trim().match(/https?:\/\/\S+/)?.[0] || (text || '').trim();
  let url: URL;
  try {
    url = new URL(/^https?:\/\//i.test(raw) ? raw : `https://${raw}`);
  } catch {
    return null;
  }
  const pick = (lat: string, lng: string) => {
    const latitude = parseFloat(lat);
    const longitude = parseFloat(lng);
    return Number.isFinite(latitude) && Number.isFinite(longitude) && Math.abs(latitude) <= 90 && Math.abs(longitude) <= 180
      ? { latitude, longitude }
      : null;
  };
  const decoded = decodeURIComponent(url.href);
  // Directions links: the last waypoint (!1d<lng>!2d<lat>) is the
  // destination — the /@ part is only the viewport center.
  if (url.pathname.startsWith('/maps/dir/')) {
    const waypoints = [...decoded.matchAll(/!1d(-?\d+\.\d+)!2d(-?\d+\.\d+)/g)];
    const last = waypoints[waypoints.length - 1];
    if (last) return pick(last[2], last[1]);
    const dest = url.searchParams.get('destination');
    const c = dest ? parseCoordinateText(dest) : null;
    if (c) return c;
  }
  const pin = decoded.match(/!3d(-?\d+\.\d+)!4d(-?\d+\.\d+)/);
  if (pin) return pick(pin[1], pin[2]);
  const at = decoded.match(/@(-?\d+\.\d+),(-?\d+\.\d+)/);
  if (at) return pick(at[1], at[2]);
  const searchPath = decoded.match(/\/maps\/(?:search|place)\/(-?\d+\.\d+),\s*\+?(-?\d+\.\d+)/);
  if (searchPath) return pick(searchPath[1], searchPath[2]);
  for (const param of ['q', 'll', 'query', 'destination', 'daddr', 'sll', 'center']) {
    const v = url.searchParams.get(param);
    const c = v ? parseCoordinateText(v.replace(/\+/g, ' ')) : null;
    if (c) return c;
  }
  return null;
}
